import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import PageIntro from "@/components/base/PageIntro";
import usePageMeta from "@/hooks/usePageMeta";
import { postItems } from "@/mocks/content";
import { formatDateTime } from "@/lib/format";
import PublishModal from "./components/PublishModal";

const LIKE_KEY = "hongtao-post-likes";

function readLikes(): string[] {
  try {
    const raw = localStorage.getItem(LIKE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistLikes(ids: string[]) {
  try {
    localStorage.setItem(LIKE_KEY, JSON.stringify(ids));
  } catch {
    // 忽略本地存储异常，仅保留当前会话状态
  }
}

const allTags = ["全部", "剧情讨论", "影片解读", "观影指南", "综艺", "幕后", "求推荐", "生活方式"];

export default function CommunityPage() {
  usePageMeta(
    "互动专区 - 红桃网",
    "红桃网互动专区，和影迷一起聊剧情、聊综艺、分享观影生活。支持话题筛选与帖子搜索，点赞与发帖为前端演示功能。",
    "互动专区,影视讨论,综艺话题,影迷社区,红桃网",
  );

  const [keyword, setKeyword] = useState("");
  const [activeTag, setActiveTag] = useState("全部");
  const [likedIds, setLikedIds] = useState<string[]>(readLikes);
  const [publishOpen, setPublishOpen] = useState(false);

  const filtered = useMemo(() => {
    const query = keyword.trim().toLowerCase();
    return postItems.filter((post) => {
      const tagOk = activeTag === "全部" || post.tags.includes(activeTag);
      const queryOk =
        !query ||
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.author.toLowerCase().includes(query) ||
        post.tags.some((tag) => tag.toLowerCase().includes(query));
      return tagOk && queryOk;
    });
  }, [keyword, activeTag]);

  const toggleLike = (id: string) => {
    setLikedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id];
      persistLikes(next);
      return next;
    });
  };

  const resetFilters = () => {
    setKeyword("");
    setActiveTag("全部");
  };

  const totalLikes = postItems.reduce((sum, post) => sum + post.likes, 0);

  return (
    <>
      <PageIntro
        kicker="互动社区"
        title="互动专区"
        description="聊刚看完的剧、分享私藏片单、吐槽让人意难平的结局。这里没有标准答案，只有认真聊过的人。"
        breadcrumbs={[{ label: "首页", to: "/" }, { label: "互动专区" }]}
        extra={
          <div className="flex items-center gap-5 text-sm">
            <div>
              <p className="font-label text-xl font-extrabold text-foreground-950">{postItems.length}</p>
              <p className="text-xs text-foreground-500">篇讨论帖</p>
            </div>
            <span className="h-8 w-px bg-background-300" />
            <div>
              <p className="font-label text-xl font-extrabold text-primary-600">{totalLikes}</p>
              <p className="text-xs text-foreground-500">次点赞</p>
            </div>
          </div>
        }
      />

      <div className="mx-auto w-full max-w-portal px-4 py-6 md:px-6 md:py-8">
        <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-chat-smile-3-line text-xl" />
              </span>
              <div>
                <p className="font-heading text-sm font-semibold text-foreground-950">欢迎来到红桃讨论区</p>
                <p className="mt-1 max-w-xl text-xs leading-relaxed text-foreground-500">
                  发帖需登录，本站暂未接入账号与后端。点赞与筛选为前端演示，刷新后点赞记录仅保留在本机浏览器。
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPublishOpen(true)}
              className="flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-edit-2-line" />
              发布帖子
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-9">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <form
                onSubmit={(event) => event.preventDefault()}
                className="flex flex-col gap-2 sm:flex-row sm:items-center"
              >
                <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2 focus-within:border-primary-400 focus-within:bg-background-50">
                  <i className="ri-search-line text-base text-foreground-400" />
                  <input
                    type="search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder="搜索帖子标题、作者或话题标签"
                    aria-label="搜索帖子"
                    className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                  />
                  {keyword && (
                    <button
                      type="button"
                      onClick={() => setKeyword("")}
                      aria-label="清空搜索"
                      className="cursor-pointer text-foreground-400 hover:text-foreground-700"
                    >
                      <i className="ri-close-circle-fill" />
                    </button>
                  )}
                </div>
                <p className="shrink-0 text-xs text-foreground-400">
                  共 <span className="font-label font-semibold text-foreground-800">{filtered.length}</span> 篇帖子
                </p>
              </form>

              <div className="mt-3 flex flex-wrap gap-1.5">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setActiveTag(tag)}
                    className={[
                      "cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors",
                      activeTag === tag
                        ? "border-primary-500 bg-primary-500 text-background-50"
                        : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600",
                    ].join(" ")}
                  >
                    {tag === "全部" ? "全部话题" : `#${tag}`}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {filtered.length > 0 ? (
                filtered.map((post) => {
                  const liked = likedIds.includes(post.id);
                  return (
                    <article
                      key={post.id}
                      className="rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5"
                    >
                      <div className="flex items-start gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 font-heading text-sm font-bold text-primary-600">
                          {post.author.slice(0, 1)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-foreground-400">
                            <span className="font-medium text-foreground-700">{post.author}</span>
                            <span className="rounded-sm bg-accent-100 px-1.5 py-0.5 text-[10px] text-accent-900">
                              {post.authorTag}
                            </span>
                            <span className="text-foreground-200">·</span>
                            <span>{formatDateTime(post.createdAt)}</span>
                          </div>

                          <h2 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950 md:text-[17px]">
                            <Link to={`/community/${post.id}`} className="transition-colors hover:text-primary-600">
                              {post.title}
                            </Link>
                          </h2>
                          <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-600 line-clamp-2">
                            {post.excerpt}
                          </p>

                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            {post.tags.map((tag) => (
                              <span
                                key={tag}
                                className="rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>

                          <div className="mt-3 flex items-center gap-4 border-t border-background-100 pt-3 text-xs text-foreground-400">
                            <button
                              type="button"
                              onClick={() => toggleLike(post.id)}
                              aria-pressed={liked}
                              className={[
                                "flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors",
                                liked
                                  ? "border-primary-400 bg-primary-50 text-primary-600"
                                  : "border-background-200 hover:border-primary-300 hover:text-primary-600",
                              ].join(" ")}
                            >
                              <i className={liked ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                              <span className="font-label">{post.likes + (liked ? 1 : 0)}</span>
                            </button>
                            <Link
                              to={`/community/${post.id}`}
                              className="flex cursor-pointer items-center gap-1.5 transition-colors hover:text-primary-600"
                            >
                              <i className="ri-chat-3-line" />
                              {post.replies} 条讨论
                            </Link>
                            <Link
                              to={`/community/${post.id}`}
                              className="ml-auto flex cursor-pointer items-center gap-0.5 transition-colors hover:text-primary-600"
                            >
                              查看帖子
                              <i className="ri-arrow-right-s-line" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })
              ) : (
                <EmptyState
                  title="没有找到相关帖子"
                  description="换个关键词，或者选择一个不同的话题标签试试。如果你愿意，也可以成为第一个发起讨论的人。"
                  actionLabel="清除筛选"
                  onAction={resetFilters}
                  icon="ri-chat-search-line"
                />
              )}
            </div>
          </div>

          <aside className="space-y-4 self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-hashtag text-primary-500" />
                热门话题
              </h2>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {["长夜将尽", "雾港", "国庆档", "综艺幕后", "悬疑剧", "家庭影院"].map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setActiveTag("全部")}
                    className="cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-600"
                  >
                    #{tag}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-foreground-400">
                话题列表为演示内容，点击后仅回到全部话题视图。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-user-star-line text-primary-500" />
                活跃用户
              </h2>
              <ul className="mt-3 space-y-2.5">
                {postItems.slice(0, 4).map((post) => (
                  <li key={post.id} className="flex items-center gap-2.5">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-background-100 text-[11px] font-semibold text-foreground-600">
                      {post.author.slice(0, 1)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] text-foreground-800">{post.author}</p>
                      <p className="text-[11px] text-foreground-400">{post.authorTag}</p>
                    </div>
                    <span className="font-label text-[11px] text-foreground-400">{post.replies} 回复</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-accent-100 p-4">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-accent-950">
                <i className="ri-shield-check-line" />
                社区小提示
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-accent-900/80">
                理性讨论、友善表达。本专区所有帖子与用户均为虚构演示数据，不涉及真实用户信息。
              </p>
            </div>
          </aside>
        </div>
      </div>

      <PublishModal open={publishOpen} onClose={() => setPublishOpen(false)} />
    </>
  );
}